import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Grid,
    IconButton,
    Link,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';

import {
    useEffect,
    useRef,
    useState
} from 'react';

import { useSelector } from 'react-redux';

import { RootState } from '../../../../reducers';

import { useAppDispatch } from '../../../../store/store';

import { localDateFormat } from '../../../../utils/timeFormat';

import b64toBlob from 'b64-to-blob';

import { parentUrl } from '../../../../utils/constants';

import WidgetLoading from '../../Shared/WidgetLoading';

import TeamDropdown from '../../../Shared/TeamDropdown';

import { fetchTeams } from '../../../../store/careTeams.slice';

import { useWidgetContext } from '../../../../hooks/widgetContext';

import { useNavigate } from 'react-router';

import {
    fetchMedicalDocumentsToReview,
    markAsReviewed,
    exportMedicalDocumentsToReview
} from '../../../Widgets/WidgetService';


const MedicalDocumentExportButton = ({
    documentTimePeriod,
    documentSelectedTeam,
    documentShowInActiveTeams,
    disabled
}) => {
    const [
        exporting,
        setExporting
    ] = useState(false);

    const handleExport = async () => {
        setExporting(true);

        try {
            await new Promise<void>(
                (resolve) => {
                    requestAnimationFrame(
                        () => {
                            resolve();
                        }
                    );
                }
            );

            const response =
                await exportMedicalDocumentsToReview(
                    documentTimePeriod,
                    documentSelectedTeam,
                    documentShowInActiveTeams
                );

            const blob = b64toBlob(
                response.base64String,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            );

            const url =
                window.URL.createObjectURL(
                    blob
                );

            const link =
                document.createElement('a');

            link.href = url;

            const date =
                new Date();

            const mm =
                date.getMonth() + 1;

            const dd =
                date.getDate();

            const dateInfo = [
                (mm > 9 ? '' : '0') + mm,
                (dd > 9 ? '' : '0') + dd,
                date.getFullYear()
            ].join('');

            link.setAttribute(
                'download',
                `MedicalDocumentsToReview - ${dateInfo}.xlsx`
            );

            document.body.appendChild(
                link
            );

            link.click();

            document.body.removeChild(
                link
            );

            window.URL.revokeObjectURL(
                url
            );
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setExporting(false);
        }
    };

    return (
        <Button
            style={{
                minWidth: '120px',
                marginLeft: 'auto'
            }}
            onClick={
                handleExport
            }
            variant="contained"
            color="success"
            disabled={
                exporting ||
                disabled
            }
        >
            {exporting
                ? 'Exporting...'
                : 'Export'}
        </Button>
    );
};


const MedicalDocumentsToReview = (props) => {
    const dispatch = useAppDispatch();

    const history = useNavigate();

    const { careTeams: teams } = useSelector(
        (state: RootState) =>
            state.careTeams
    );

    const [
        recentMedicalDocuments,
        setRecentMedicalDocuments
    ] = useState([]);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        show,
        setShow
    ] = useState(false);

    const ref = useRef(null);

    const {
        documentTimePeriod,
        setDocumentTimePeriod,
        documentSelectedTeam,
        setDocumentSelectedTeam,
        documentShowInActiveTeams,
        setDocumentShowInActiveTeams
    } = useWidgetContext();

    useEffect(() => {
        dispatch(fetchTeams(true));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () => {
        setShow(!show);
    };

    const timePeriods = [
        {
            label: '7 Days',
            value: 1
        },
        {
            label: '15 Days',
            value: 2
        },
        {
            label: '30 Days',
            value: 3
        }
    ];

    const handleChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
        id: string
    ) => {
        try {
            await markAsReviewed(id);

            setRecentMedicalDocuments(
                (previous) =>
                    previous.filter(
                        (item) =>
                            item.Id !== id
                    )
            );
        }
        catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const changeFilter =
            async () => {
                try {
                    setIsLoading(true);

                    const response =
                        await fetchMedicalDocumentsToReview(
                            documentTimePeriod,
                            documentSelectedTeam,
                            documentShowInActiveTeams
                        );

                    if (!isMounted) {
                        return;
                    }

                    setRecentMedicalDocuments(
                        response ?? []
                    );
                }
                catch (error) {
                    if (isMounted) {
                        setRecentMedicalDocuments(
                            []
                        );
                    }

                    console.error(
                        error
                    );
                }
                finally {
                    if (isMounted) {
                        setIsLoading(
                            false
                        );
                    }
                }
            };

        changeFilter();

        return () => {
            isMounted = false;
        };
    }, [
        documentTimePeriod,
        documentSelectedTeam,
        documentShowInActiveTeams
    ]);

    const navigateToMembers = () => {
        history(
            '/provider/metricsummary/medicaldocumentstoreviewdetail'
        );
    };

    return (
        <Grid
            size={{
                xs: 12,
                lg: 6
            }}
        >
            <Paper
                className="smartdashboard-widget"
                elevation={5}
                sx={{
                    height:
                        'calc(100vh - 140px)',
                    maxHeight:
                        '650px',
                    display:
                        'flex',
                    flexDirection:
                        'column',
                    overflow:
                        'auto'
                }}
            >
                <Grid
                    container
                    spacing={2}
                >
                    <Grid
                        size={12}
                        textAlign="center"
                    >
                        <Typography
                            display="inline-block"
                            variant="h6"
                            color="primary"
                        >
                            <span
                                className="pointer"
                                onClick={
                                    navigateToMembers
                                }
                            >
                                Medical Documents to Review
                            </span>
                        </Typography>

                        <Typography
                            display="inline-block"
                            variant="body1"
                            color="primary"
                            ref={ref}
                        >
                            &#40;
                            {
                                isLoading
                                    ? 0
                                    : recentMedicalDocuments.length
                            }
                            &#41;

                            <Tooltip
                                open={
                                    show
                                }
                                title={
                                    <div>
                                        <Typography
                                            variant="caption"
                                        >
                                            Use this widget to review recent documents added to the member’s chart and take any needed follow-up steps.
                                        </Typography>
                                    </div>
                                }
                                PopperProps={{
                                    style: {
                                        zIndex:
                                            1000
                                    }
                                }}
                            >
                                <IconButton
                                    onClick={
                                        handleClick
                                    }
                                >
                                    <InfoIcon
                                        sx={{
                                            width:
                                                '18px',
                                            height:
                                                '18px'
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>

                    {props.hasElevatedFilterAccess && (
                        <Grid
                            size={4}
                        >
                            <TeamDropdown
                                teams={
                                    teams
                                }
                                selectedTeam={
                                    documentSelectedTeam
                                }
                                onChange={(
                                    id
                                ) => {
                                    setIsLoading(
                                        true
                                    );

                                    setDocumentSelectedTeam(
                                        id
                                    );
                                }}
                                showInactive={
                                    documentShowInActiveTeams
                                }
                                onToggleInactive={(
                                    value
                                ) => {
                                    setIsLoading(
                                        true
                                    );

                                    setDocumentShowInActiveTeams(
                                        value
                                    );
                                }}
                            />
                        </Grid>
                    )}

                    <Grid
                        size={5.5}
                        paddingLeft={1}
                    >
                        <ToggleButtonGroup
                            value={
                                documentTimePeriod
                            }
                            exclusive
                            disabled={
                                documentSelectedTeam ===
                                '-1'
                            }
                            size="small"
                            onChange={(
                                event:
                                    React.MouseEvent<HTMLElement>,
                                newPeriod:
                                    number | null
                            ) => {
                                if (
                                    newPeriod ===
                                    null
                                ) {
                                    return;
                                }

                                setIsLoading(
                                    true
                                );

                                setDocumentTimePeriod(
                                    newPeriod
                                );
                            }}
                        >
                            {timePeriods.map(
                                (
                                    period
                                ) => {
                                    return (
                                        <ToggleButton
                                            color="primary"
                                            key={
                                                period.value
                                            }
                                            value={
                                                period.value
                                            }
                                        >
                                            {
                                                period.label
                                            }
                                        </ToggleButton>
                                    );
                                }
                            )}
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid
                        size={2}
                        maxHeight="55px"
                    >
                        <MedicalDocumentExportButton
                            documentTimePeriod={
                                documentTimePeriod
                            }
                            documentSelectedTeam={
                                documentSelectedTeam
                            }
                            documentShowInActiveTeams={
                                documentShowInActiveTeams
                            }
                            disabled={
                                recentMedicalDocuments.length ===
                                0
                            }
                        />
                    </Grid>
                </Grid>

                <Box
                    display="flex"
                    position="relative"
                    height="calc(100vh - 285px)"
                >
                    {isLoading ? (
                        <WidgetLoading />
                    ) : recentMedicalDocuments.length >
                      0 ? (
                        <Grid
                            container
                            spacing={2}
                            marginTop={3}
                            sx={{
                                height:
                                    '100%',
                                maxHeight:
                                    '500px',
                                overflowY:
                                    'auto',
                                position:
                                    'relative',
                                width:
                                    '100%'
                            }}
                        >
                            {recentMedicalDocuments.map(
                                (
                                    member
                                ) => (
                                    <Grid
                                        key={
                                            member.Id
                                        }
                                        size={{
                                            xs: 12,
                                            sm: 6
                                        }}
                                        sx={{
                                            contentVisibility:
                                                'auto',
                                            containIntrinsicSize:
                                                '180px'
                                        }}
                                    >
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height:
                                                    '100%',
                                                boxShadow:
                                                    0,
                                                borderRadius:
                                                    2,
                                                transition:
                                                    'box-shadow 0.2s ease, border-color 0.2s ease',
                                                '&:hover':
                                                    {
                                                        boxShadow:
                                                            2,
                                                        borderColor:
                                                            'primary.light'
                                                    }
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    p: 2,
                                                    '&:last-child':
                                                        {
                                                            pb: 2
                                                        }
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    spacing={
                                                        1.5
                                                    }
                                                >
                                                    <Grid
                                                        size={
                                                            6
                                                        }
                                                    >
                                                        <Typography
                                                            color="text.secondary"
                                                            variant="caption"
                                                        >
                                                            Member
                                                        </Typography>

                                                        <Typography
                                                            fontWeight={
                                                                600
                                                            }
                                                            variant="body2"
                                                            sx={{
                                                                cursor:
                                                                    'pointer',
                                                                '&:hover':
                                                                    {
                                                                        textDecoration:
                                                                            'underline'
                                                                    }
                                                            }}
                                                            onClick={() =>
                                                                window.open(
                                                                    `${parentUrl}provider/members-list/member-chart/${member.MemberId}/documents`,
                                                                    '_blank'
                                                                )
                                                            }
                                                        >
                                                            {
                                                                member.MemberName
                                                            }
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        size={
                                                            6
                                                        }
                                                    >
                                                        <Typography
                                                            color="text.secondary"
                                                            variant="caption"
                                                        >
                                                            DOB
                                                        </Typography>

                                                        <Typography
                                                            fontWeight={
                                                                600
                                                            }
                                                            variant="body2"
                                                        >
                                                            {member.DOB
                                                                ? localDateFormat(
                                                                      new Date(
                                                                          member.DOB
                                                                      )
                                                                  )
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        size={
                                                            6
                                                        }
                                                    >
                                                        <Typography
                                                            color="text.secondary"
                                                            variant="caption"
                                                        >
                                                            Uploaded On
                                                        </Typography>

                                                        <Typography
                                                            fontWeight={
                                                                600
                                                            }
                                                            variant="body2"
                                                        >
                                                            {member.CreationTime
                                                                ? localDateFormat(
                                                                      new Date(
                                                                          member.CreationTime
                                                                      )
                                                                  )
                                                                : '-'}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        size={
                                                            6
                                                        }
                                                    >
                                                        <Typography
                                                            color="text.secondary"
                                                            variant="caption"
                                                        >
                                                            Uploaded By
                                                        </Typography>

                                                        <Typography
                                                            fontWeight={
                                                                600
                                                            }
                                                            variant="body2"
                                                        >
                                                            {member.UploadedBy
                                                                ?.Name ||
                                                                '-'}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        size={
                                                            6
                                                        }
                                                    >
                                                        <Typography
                                                            color="text.secondary"
                                                            variant="caption"
                                                        >
                                                            Document Name
                                                        </Typography>

                                                        <Link
                                                            href={
                                                                member.AzureDocumentUri
                                                            }
                                                            target="_blank"
                                                            onClick={(
                                                                e
                                                            ) =>
                                                                e.stopPropagation()
                                                            }
                                                            variant="body2"
                                                            sx={{
                                                                display:
                                                                    'block',
                                                                fontWeight:
                                                                    600,
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap'
                                                            }}
                                                        >
                                                            {member.Name ||
                                                                '-'}
                                                        </Link>
                                                    </Grid>

                                                    <Grid
                                                        size={
                                                            6
                                                        }
                                                        display="flex"
                                                        alignItems="center"
                                                    >
                                                        <Typography
                                                            fontWeight={
                                                                600
                                                            }
                                                            variant="body2"
                                                        >
                                                            Mark As Reviewed
                                                        </Typography>

                                                        <Checkbox
                                                            checked={
                                                                false
                                                            }
                                                            onClick={(
                                                                e
                                                            ) =>
                                                                e.stopPropagation()
                                                            }
                                                            onChange={(
                                                                e
                                                            ) =>
                                                                handleChange(
                                                                    e,
                                                                    member.Id
                                                                )
                                                            }
                                                            size="small"
                                                            sx={{
                                                                p: 0.5,
                                                                ml: 0.5
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )
                            )}
                        </Grid>
                    ) : (
                        <Box
                            display="flex"
                            width="100%"
                            height="100%"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Typography
                                variant="h6"
                            >
                                No Matching Records
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Grid>
    );
};

export default MedicalDocumentsToReview;